<template>
  <div class="col-xl-12">
    <div class="card stock-dashboard-card" ref="stockCard">
      <!-- HEADER -->
      <div class="card-header card-no-border">
        <div class="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
          <div class="d-flex flex-column align-items-start">
            <h5>VEHICLE TARGET VS ACHIEVEMENT</h5>
            <p class="target-subtitle"> {{ currentDateDisplay }} </p>
          </div>

          <div class="d-flex align-items-center gap-2">
            <div
              class="refresh-icon"
              @click="handleRefresh"
              :class="{ disabled: loading }"
              title="Refresh"
            >
              <i :class="loading ? 'fa fa-spinner fa-spin' : 'fa fa-sync'"></i>
            </div>

            <div class="icon-btn" @click="toggleFullScreen" title="Fullscreen">
              <i :class="isFullScreen ? 'fa fa-minimize' : 'fa fa-maximize'"></i>
            </div>
          </div>
        </div>
        <div class="d-flex justify-content-start align-items-start flex-wrap gap-2">
          <select v-model="itemStatusFilter" class="form-select" style="width: 130px">
            <option value="all">All items</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </select>
        </div>
      </div>

      <!-- BODY -->
      <div class="card-body pt-0">
        <div v-if="loading" class="spinner-container">
          <div class="spinner"></div>
        </div>

        <div v-else-if="error" class="error-container">
          <p class="error-message">{{ error }}</p>
          <button class="stock-retry-btn" @click="fetchStockData" :disabled="loading">
            Retry
          </button>
        </div>

        <div v-else-if="!rows.length || !visibleItems.length" class="empty-container">
          <p class="empty-message">No target/achievement data available for the selected distributor.</p>
        </div>

        <div v-else class="stock-table-wrap" :style="{ maxHeight: tableMaxHeight + 'px' }">
          <table class="stock-table">
            <thead>
              <!-- Item variant group row -->
              <tr>
                <th class="se-col sticky-col" rowspan="2">Vehicle</th>

                <th
                  v-for="item in visibleItems"
                  :key="item.variant_id"
                  class="item-group-col"
                  :class="{ 'inactive-item': !item.is_active }"
                  colspan="5"
                >
                  {{ item.variant_name }}
                  <span v-if="!item.is_active" class="inactive-badge">Inactive</span>
                </th>
              </tr>

              <!-- Target / Completed / Balance / Bal% row -->
              <tr>
                <template v-for="item in visibleItems" :key="item.variant_id + '-sub'">
                  <th class="sub-col target-col">Target</th>
                  <th class="sub-col completed-col">Completed</th>
                  <th class="sub-col balance-col">Balance</th>
                  <th class="sub-col pct-col">Bal %</th>
                  <th class="sub-col avgday-col">Bal Avg/Day</th>
                </template>
              </tr>
            </thead>

            <tbody>
              <tr v-for="row in rows" :key="row.vehicle_profile_id">
                <td class="se-col sticky-col">{{ row.vehicle_name }}</td>

                <template v-for="cell in visibleCells(row)" :key="cell.variant_id">
                  <td class="qty-cell target-col" :class="{ zero: !cell.target }">
                    {{ formatQty(cell.target) }}
                  </td>
                  <td class="qty-cell completed-col" :class="{ zero: !cell.completed }">
                    {{ formatQty(cell.completed) }}
                  </td>
                  <td
                    class="qty-cell balance-col"
                    :class="{ zero: !cell.balance, shortage: cell.balance > 0, over: cell.balance < 0 }"
                  >
                    {{ formatQty(cell.balance) }}
                  </td>
                  <td
                    class="qty-cell pct-col"
                    :class="{ zero: !cell.target, short: cell.balance_percentage > 0 }"
                  >
                    {{ formatPct(cell.balance_percentage) }}
                  </td>
                  <td
                    class="qty-cell avgday-col"
                    :class="{ zero: !cell.balance, shortage: cell.balance > 0, over: cell.balance < 0 }"
                    >
                    {{ formatQty(balanceAvgPerDay(cell.balance)) }}
                </td>
                </template>
              </tr>
            </tbody>

            <tfoot>
              <tr>
                <td class="se-col sticky-col">Total</td>

                <template v-for="item in visibleItems" :key="item.variant_id + '-footer'">
                    <td class="qty-cell target-col footer-cell">
                        {{ formatQty(columnTotal(item.variant_id, 'target')) }}
                    </td>
                    <td class="qty-cell completed-col footer-cell">
                        {{ formatQty(columnTotal(item.variant_id, 'completed')) }}
                    </td>
                    <td class="qty-cell balance-col footer-cell">
                        {{ formatQty(columnBalanceTotal(item.variant_id)) }}
                    </td>
                    <td class="qty-cell pct-col footer-cell">
                        {{ formatPct(columnPctTotal(item.variant_id)) }}
                    </td>
                    <td class="qty-cell avgday-col footer-cell">
                        {{ formatQty(columnBalanceTotal(item.variant_id) / remainingDays) }}
                    </td>
                </template>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fetchDistributors,
  fetchVehicleTargetsAchievements,
} from "@/services/dataService";
import { getApiErrorMessage } from "@/utils/apiError";

export default {
  data() {
    const userLabel = localStorage.getItem("user_label");
    return {
      token: localStorage.getItem("token_set"),
      profileId: localStorage.getItem("profile_id"),
      reportingProfileId: localStorage.getItem("reportingL1ProfileId") || "",
      isDistributor: userLabel === "DISTRIBUTOR",
      isDistributorBackOffice: userLabel === "DISTRIBUTOR BACK OFFICE",

      loading: false,
      error: null,
      isFullScreen: false,
      tableMaxHeight: 360,

      distributors: [],
      selectedDistributor: "",

      items: [],
      rows: [],
      itemStatusFilter: "all",

      distributorsLoaded: false,
      distributorRequestPromise: null,
      statsRequestSequence: 0,
      componentActive: true,
    };
  },

  computed: {
    visibleItems() {
      const list = this.items.map((i) => ({
        ...i,
        is_active: i.is_active !== false,
      }));

      const filtered = list.filter((i) => {
        if (this.itemStatusFilter === "active") return i.is_active;
        if (this.itemStatusFilter === "inactive") return !i.is_active;
        return true;
      });

      return filtered;
    },

    remainingDays() {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const today = now.getDate();

      return Math.max(daysInMonth - today + 1, 1);
    },

    currentDateDisplay() {
      const now = new Date();
      return now.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },

  mounted() {
    if (this.isDistributor) {
      this.selectedDistributor = this.profileId;
      this.lazyLoadDistributors();
    } else if (this.isDistributorBackOffice) {
      this.selectedDistributor = this.reportingProfileId;
      this.lazyLoadDistributors();
    }

    this.tableMaxHeight = this.isFullScreen
      ? window.innerHeight - 160
      : 360;

    this.fetchStockData();

    document.addEventListener("fullscreenchange", this.handleFullscreenChange);
  },

  beforeUnmount() {
    this.componentActive = false;
    this.statsRequestSequence += 1;

    document.removeEventListener("fullscreenchange", this.handleFullscreenChange);
  },

  methods: {
    handleFullscreenChange() {
      this.isFullScreen = !!document.fullscreenElement;

      this.$nextTick(() => {
        this.tableMaxHeight = this.isFullScreen
          ? window.innerHeight - 160
          : 360;
      });
    },

    async lazyLoadDistributors() {
      if (this.distributorsLoaded) {
        return;
      }

      if (this.distributorRequestPromise) {
        await this.distributorRequestPromise;
        return;
      }

      this.distributorRequestPromise = this.fetchDistributorList();

      try {
        const loaded = await this.distributorRequestPromise;

        if (loaded) {
          this.distributorsLoaded = true;
        }
      } finally {
        this.distributorRequestPromise = null;
      }
    },

    async fetchDistributorList() {
      try {
        const response = await fetchDistributors(this.profileId, this.token);

        if (!this.componentActive) {
          return false;
        }

        this.distributors = Array.isArray(response.data?.data)
          ? response.data.data
          : [];

        return true;
      } catch (error) {
        console.error(
          "Distributor list API failed:",
          error?.response?.status,
          error?.response?.data || error
        );

        return false;
      }
    },

    handleRefresh() {
      if (this.loading) {
        return;
      }

      this.fetchStockData();
    },

    async fetchStockData() {
      const requestId = ++this.statsRequestSequence;

      try {
        this.loading = true;
        this.error = null;

        const payload = {
          profile_id: this.selectedDistributor || this.profileId,
        };

        const response = await fetchVehicleTargetsAchievements(payload, this.token);

        if (!this.componentActive || requestId !== this.statsRequestSequence) {
          return;
        }

        if (response.data.success) {
          this.items = response.data.items || [];
          this.rows = response.data.data || [];
        } else {
          this.items = [];
          this.rows = [];
          this.error = response.data.message || "No data available";
        }
      } catch (error) {
        if (!this.componentActive || requestId !== this.statsRequestSequence) {
          return;
        }

        console.error(
          "Vehicle target achievement API failed:",
          error?.response?.status,
          error?.response?.data || error
        );

        this.error = getApiErrorMessage(error);
      } finally {
        if (this.componentActive && requestId === this.statsRequestSequence) {
          this.loading = false;
        }
      }
    },

    visibleCells(row) {
      const cellByVariant = new Map((row.stock || []).map((c) => [c.variant_id, c]));
      return this.visibleItems
        .map((i) => cellByVariant.get(i.variant_id))
        .filter(Boolean);
    },

    columnTotal(variantId, field) {
      return this.rows.reduce((sum, row) => {
        const cell = (row.stock || []).find((c) => c.variant_id === variantId);
        return sum + Number(cell?.[field] || 0);
      }, 0);
    },

    columnBalanceTotal(variantId) {
      return this.columnTotal(variantId, "target") - this.columnTotal(variantId, "completed");
    },

    columnPctTotal(variantId) {
      const target = this.columnTotal(variantId, "target");
      const balance = this.columnBalanceTotal(variantId);
      return target > 0 ? (balance / target) * 100 : 0;
    },

    formatQty(value) {
      const num = Number(value || 0);
      return num % 1 === 0
        ? num.toLocaleString()
        : num.toLocaleString(undefined, { maximumFractionDigits: 2 });
    },

    formatPct(value) {
      const num = Number(value || 0);
      return `${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
    },

    toggleFullScreen() {
      const element = this.$refs.stockCard;

      if (!document.fullscreenElement) {
        element.requestFullscreen?.();
      } else {
        document.exitFullscreen?.();
      }
    },

    balanceAvgPerDay(balance) {
      return Number(balance || 0) / this.remainingDays;
    },
  },
};
</script>

<style scoped>
.stock-dashboard-card {
  height: 100%;
  border: 1px solid #e8edf4;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.stock-dashboard-card .card-header {
  padding: 10px 14px 8px !important;
  border-bottom: 1px solid #eef2f7;
  background: #ffffff;
}

.stock-dashboard-card .card-header h5 {
  margin: 0;
  color: #0f172a;
  font-size: 13px;
  font-weight: 900;
  letter-spacing: -0.01em;
}

.stock-dashboard-card .card-body {
  padding: 8px 14px 12px !important;
}

.header-top {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.target-subtitle {
  margin: 3px 0 0;
  font-size: 11px;
  font-weight: 400;
  color: #8a94a6;
  line-height: 1.3;
}

select {
  width: auto;
  font-size: 15px;
}

.refresh-icon,
.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  color: #6c757d;
  transition: all 0.2s ease;
  border: none;
  background: transparent;
  padding: 0;
}

.refresh-icon:hover,
.icon-btn:hover {
  background: #f1f3f5;
  color: #000;
}

.refresh-icon.disabled {
  pointer-events: none;
  opacity: 0.5;
}

.spinner-container {
  display: flex;
  justify-content: center;
  height: 200px;
  align-items: center;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #6c5ce7;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}

.error-container,
.empty-container {
  text-align: center;
  padding: 30px 10px;
}

.error-message {
  color: red;
  font-weight: bold;
}

.empty-message {
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
}

.stock-retry-btn {
  min-height: 30px;
  margin-top: 8px;
  padding: 5px 12px;
  border: 1px solid #dbe2ea;
  border-radius: 8px;
  background: #ffffff;
  color: #334155;
  font-size: 11.5px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.stock-retry-btn:hover:not(:disabled) {
  border-color: #7064f5;
  color: #7064f5;
  background: #f8f7ff;
}

.stock-retry-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* Table */
.stock-table-wrap {
  overflow-x: auto;
  overflow-y: auto;
  border: 1px solid #e8edf4;
  border-radius: 10px;
}

.stock-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.stock-table th,
.stock-table td {
  box-sizing: border-box;
}

.stock-table thead th {
  position: sticky;
  z-index: 2;
  background: #f8fafc;
  color: #334155;
  font-weight: 800;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  height: 30px;
  padding: 0 8px;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  text-align: center;
  vertical-align: middle;
}

.stock-table thead tr:nth-child(1) th {
  top: 0;
}

.stock-table thead tr:nth-child(2) th {
  top: 30px;
}

.stock-table thead th.se-col {
  text-align: left;
}

.item-group-col {
  background: #f1edff;
  color: #6c5ce7;
  border-left: 2px solid #d8ccff;
}

.sub-col {
  font-size: 10px;
  font-weight: 700;
  min-width: 68px;
  border-left: 1px solid #e2e8f0;
}

.stock-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
}

.se-col {
  text-align: left;
  font-weight: 700;
  color: #0f172a;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #ffffff;
  width: 200px;
  min-width: 200px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stock-table thead th.sticky-col {
  z-index: 4;
  background: #f8fafc;
}

.qty-cell {
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: #1f2937;
  border-left: 1px solid #f1f5f9;
}

.qty-cell.zero {
  color: #cbd5e1;
}

.qty-cell.shortage {
  color: #ef4444;
  font-weight: 700;
}

.qty-cell.over {
  color: #2e8b57;
  font-weight: 700;
}

.qty-cell.short {
  color: #ef4444;
  font-weight: 700;
}

.footer-cell {
  font-weight: 800;
  background: #f8fafc;
  color: #0f172a;
}

.stock-table tfoot td {
  position: sticky;
  bottom: 0;
  z-index: 3;
}

tfoot tr td.sticky-col {
  z-index: 5;
  background: #f8fafc;
}

.item-group-col.inactive-item {
  background: #fef2f2;
  color: #dc2626;
  border-left-color: #fecaca;
}

.inactive-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  background: #fee2e2;
  color: #b91c1c;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.02em;
  vertical-align: middle;
}
</style>